import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Trophy } from 'lucide-react';
import api from '@/lib/api';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';

/* Challenges für Mitglieder (13.10.2026, gap.md Welle 2): Die Leitung legt an und veröffentlicht, Mitglieder treten in
   der App bei. Gezählt wird aus Check-ins, Kraftlog oder Gewohnheiten. In der App-Bestenliste steht der Name nur mit
   Einwilligung; hier sieht das Team alle Namen. */

const PHASE = { kommt: 'startet bald', laeuft: 'läuft', beendet: 'beendet', abgesagt: 'abgesagt' };
// E16 (15.10.2026): mehrere Wertungen (`arten`), Prämienpunkte je Punkt, anonyme Teilnahme erlaubt — nie Gewicht oder kcal
const leer = { titel: '', beschreibung: '', art: 'checkins', arten: ['checkins'], ziel: 12, von: '', bis: '', bestenliste: true, praemien_je_punkt: 0, anonym_erlaubt: true };
const datum = (iso) => `${iso.slice(8, 10)}.${iso.slice(5, 7)}.`;
const fehlerText = (e, rest) => (typeof e?.response?.data?.detail === 'string' ? e.response.data.detail : rest);

export default function MitgliederChallenges() {
  const [d, setD] = useState(null);
  const [auf, setAuf] = useState(false);
  const [neu, setNeu] = useState(null);
  const [liste, setListe] = useState({});       // challenge_id → Bestenliste
  const lade = useCallback(() => api.get('/mitglieder-challenges').then((r) => setD(r.data)).catch(() => setD({ challenges: [], arten: {} })), []);
  useEffect(() => { if (auf) lade(); }, [auf, lade]);

  const anlegen = async () => {
    try { await api.post('/mitglieder-challenges', { ...neu, ziel: Number(neu.ziel), praemien_je_punkt: Number(neu.praemien_je_punkt || 0) }); toast.success('Angelegt – noch nicht veröffentlicht'); setNeu(null); lade(); }
    catch (e) { toast.error(fehlerText(e, 'Nicht angelegt')); }
  };
  const setzen = async (c, felder, ok) => {
    try { await api.patch(`/mitglieder-challenges/${c.id}`, felder); toast.success(ok); lade(); }
    catch (e) { toast.error(fehlerText(e, 'Nicht gespeichert')); }
  };
  const zeigen = async (c) => {
    if (liste[c.id]) { setListe((l) => ({ ...l, [c.id]: null })); return; }
    try { const r = await api.get(`/mitglieder-challenges/${c.id}/bestenliste`); setListe((l) => ({ ...l, [c.id]: r.data })); }
    catch (e) { toast.error(fehlerText(e, 'Nicht geladen')); }
  };

  return (
    <Card className="border-border/60 print:hidden" data-testid="mitglieder-challenges">
      <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0 pb-2">
        <div>
          <CardTitle className="flex items-center gap-2 text-base"><Trophy className="h-4 w-4 text-primary" /> Challenges für Mitglieder</CardTitle>
          <p className="text-xs text-muted-foreground">Z. B. „12 Trainingstage im November“. Mitglieder treten in der App bei; Namen in der Bestenliste nur mit Einwilligung.</p>
        </div>
        <Button size="sm" variant="outline" onClick={() => setAuf((v) => !v)} aria-expanded={auf}>{auf ? 'Zuklappen' : 'Öffnen'}</Button>
      </CardHeader>
      {auf && (
        <CardContent className="space-y-3">
          {!neu && <Button size="sm" onClick={() => setNeu({ ...leer })}>Neue Challenge</Button>}
          {neu && d && (
            <div className="grid gap-2 rounded-md border border-border p-3 sm:grid-cols-2">
              <Input aria-label="Titel" placeholder="Titel" maxLength={80} value={neu.titel} onChange={(e) => setNeu({ ...neu, titel: e.target.value })} className="sm:col-span-2" />
              <Input aria-label="Beschreibung" placeholder="Beschreibung (optional)" maxLength={500} value={neu.beschreibung} onChange={(e) => setNeu({ ...neu, beschreibung: e.target.value })} className="sm:col-span-2" />
              <select aria-label="Was zählt" className="h-10 rounded-md border border-input bg-background px-2 text-sm" value={neu.art} onChange={(e) => setNeu({ ...neu, art: e.target.value, arten: [e.target.value, ...neu.arten.filter((a) => a !== e.target.value)] })}>
                {Object.entries(d.arten).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
              <Input aria-label="Ziel" type="number" min={1} value={neu.ziel} onChange={(e) => setNeu({ ...neu, ziel: e.target.value })} />
              <fieldset className="col-span-full">
                <legend className="text-xs font-medium mb-1">Weitere Wertungen (je Tag oder Einheit ein Punkt; Gewicht und kcal zählen nie)</legend>
                <div className="flex flex-wrap gap-x-3 gap-y-1">
                  {Object.entries(d.arten).filter(([k]) => k !== neu.art).map(([k, v]) => (
                    <label key={k} className="flex items-center gap-1.5 text-xs">
                      <input type="checkbox" checked={neu.arten.includes(k)} disabled={!neu.arten.includes(k) && neu.arten.length >= 5}
                        onChange={(e) => setNeu({ ...neu, arten: e.target.checked ? [...neu.arten, k] : neu.arten.filter((a) => a !== k) })} />{v}
                    </label>
                  ))}
                </div>
              </fieldset>
              <label className="text-xs">Prämienpunkte je Punkt<Input type="number" min={0} max={100} value={neu.praemien_je_punkt} onChange={(e) => setNeu({ ...neu, praemien_je_punkt: e.target.value })} /></label>
              <label className="flex items-center gap-1.5 text-xs self-end"><input type="checkbox" checked={neu.anonym_erlaubt} onChange={(e) => setNeu({ ...neu, anonym_erlaubt: e.target.checked })} /> anonyme Teilnahme erlaubt</label>
              <label className="text-xs">Start<Input type="date" value={neu.von} onChange={(e) => setNeu({ ...neu, von: e.target.value })} /></label>
              <label className="text-xs">Ende<Input type="date" value={neu.bis} onChange={(e) => setNeu({ ...neu, bis: e.target.value })} /></label>
              <label className="flex items-center gap-2 text-sm sm:col-span-2"><input type="checkbox" className="accent-marke" checked={neu.bestenliste} onChange={(e) => setNeu({ ...neu, bestenliste: e.target.checked })} /> Bestenliste in der App zeigen</label>
              <div className="flex justify-end gap-2 sm:col-span-2">
                <Button size="sm" variant="ghost" onClick={() => setNeu(null)}>Abbrechen</Button>
                <Button size="sm" onClick={anlegen} disabled={!neu.titel.trim() || !neu.von || !neu.bis}>Anlegen</Button>
              </div>
            </div>
          )}
          {d && !d.challenges.length && <p className="text-sm text-muted-foreground">Noch keine Challenge.</p>}
          {d && d.challenges.map((c) => (
            <div key={c.id} className="space-y-2 border-t border-border/50 pt-3">
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-semibold">{c.titel}</span>
                <Badge variant="secondary" className="text-[10px]">{PHASE[c.phase]}</Badge>
                {!c.veroeffentlicht && <Badge variant="outline" className="text-[10px]">nicht veröffentlicht</Badge>}
                <span className="text-xs text-muted-foreground">{c.ziel} {c.art_text} · {datum(c.von)}–{datum(c.bis)} · {c.teilnehmende} dabei</span>
                <span className="ml-auto flex gap-1">
                  {!c.veroeffentlicht && c.phase !== 'beendet' && <Button size="sm" variant="outline" onClick={() => setzen(c, { veroeffentlicht: true }, 'Veröffentlicht – Mitglieder sehen sie in der App')}>Veröffentlichen</Button>}
                  {c.phase !== 'beendet' && !c.abgesagt && c.veroeffentlicht && <Button size="sm" variant="ghost" onClick={() => window.confirm('Challenge absagen?') && setzen(c, { abgesagt: true }, 'Abgesagt')}>Absagen</Button>}
                  <Button size="sm" variant="ghost" onClick={() => zeigen(c)}>{liste[c.id] ? 'Liste zu' : 'Teilnehmende'}</Button>
                </span>
              </div>
              {liste[c.id] && (
                <ol className="space-y-0.5 text-sm">
                  {liste[c.id].zeilen.map((z) => <li key={z.lead_id} className="tabular-nums">{z.platz}. {z.name} – {z.stand}{z.geschafft ? ' ✓' : ''}</li>)}
                  {!liste[c.id].zeilen.length && <li className="text-muted-foreground">Noch niemand dabei.</li>}
                </ol>
              )}
            </div>
          ))}
        </CardContent>
      )}
    </Card>
  );
}
